import React from "react";
import type { GPUInstanceType } from "types/InstanceTypes";
import { isStoppableInstance } from "components/Environment/Create/utils";
import {
  getBasePriceAmount,
  getStoragePricing,
  isFreeTierInstance,
  useFreeTier,
  withServiceCharge,
} from "utils/freeTierUtils";
import { PriceSection } from "../deployment/PriceSection";

interface UseDeployPriceOptions {
  instanceType: GPUInstanceType | null;
  instanceStorage: string | null;
  intendedDiskStorage: string | null;
  priceDisclaimer?: string;
  priceLabel?: string;
  location?: string | null;
}

export const useDeployPrice = ({
  instanceType,
  instanceStorage,
  intendedDiskStorage,
  priceDisclaimer,
  priceLabel,
  location,
}: UseDeployPriceOptions) => {
  const storageGiB = Number(instanceStorage || intendedDiskStorage || 0);
  const { isEntitled } = useFreeTier();
  const basePriceAmount = getBasePriceAmount(
    instanceType ?? undefined,
    location,
  );
  const isFreeTier = isEntitled && isFreeTierInstance(basePriceAmount);
  const computePrice = withServiceCharge(basePriceAmount);
  const {
    pricePerGbHr,
    totalCost: rawStorageCost,
    withinFreeTier: isStorageWithinFreeTier,
  } = getStoragePricing(instanceType ?? undefined, storageGiB);
  const storageCost =
    instanceType?.elastic_root_volume && pricePerGbHr > 0 && storageGiB > 0
      ? rawStorageCost
      : 0;
  const sharedPriceProps = {
    displayPrice: (computePrice + storageCost).toFixed(2),
    disclaimer: priceDisclaimer,
    priceLabel,
    hasPrice: Boolean(instanceType),
    isPriceSettled: Boolean(
      instanceType && (!instanceType.elastic_root_volume || storageGiB > 0),
    ),
    isFreeTier,
    isEntitled,
    computePrice,
    storageCost,
    pricePerGbHr,
    isStorageFree: isEntitled && isStorageWithinFreeTier,
    stoppable: isStoppableInstance(instanceType?.stoppable),
    inline: true,
  };

  return {
    estimatedCost: <PriceSection {...sharedPriceProps} />,
    stickyEstimatedCost: <PriceSection {...sharedPriceProps} compact />,
  };
};
